import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setCachedUser } from "@/lib/authUser";
import { coordinateAuthRecovery } from "@/lib/authRequest";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  /** True until the initial session has been restored from storage. */
  initializing: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  initializing: true,
});

/**
 * Single source of truth for auth state.
 *
 * Route guards and hooks must read from here instead of calling
 * `supabase.auth.getUser()` / `getSession()` themselves: every one of those
 * calls contends on the auth client's internal session lock, and a page that
 * mounts a dozen of them at once can stall the whole app right after sign-in.
 */
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);
  const settledRef = useRef(false);
  const authEventVersionRef = useRef(0);
  const lastUserIdRef = useRef<string | null>(null);
  const currentSessionRef = useRef<Session | null>(null);

  useEffect(() => {
    let isMounted = true;

    const settle = (next: Session | null) => {
      if (!isMounted) return;
      if (next?.user.id) {
        lastUserIdRef.current = next.user.id;
      } else {
        const lastUserId = lastUserIdRef.current;
        localStorage.removeItem("last_dashboard_workspace");
        localStorage.removeItem("user_role");
        if (lastUserId) localStorage.removeItem(`last_dashboard_workspace:${lastUserId}`);
        lastUserIdRef.current = null;
      }
      setSession(next);
      currentSessionRef.current = next;
      setCachedUser(next?.user ?? null);
      if (!settledRef.current) {
        settledRef.current = true;
        setInitializing(false);
      }
    };

    // Listener first so no auth event emitted during restore is missed. The
    // INITIAL_SESSION event is intentionally left to the restore below: an
    // expired session from storage must be refreshed before protected routes
    // and their data requests are allowed to mount.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === "INITIAL_SESSION") return;

      authEventVersionRef.current += 1;

      if (event === "SIGNED_OUT") {
        settle(null);
        return;
      }

      // Preserve the last valid session if the auth client emits a transient
      // null payload while refreshing after a background-tab pause.
      if (!nextSession) {
        if (!settledRef.current) settle(null);
        return;
      }

      // Realtime sockets retain the token they connected with. Rotate it with
      // the session so protected lead updates survive a background-tab resume.
      supabase.realtime.setAuth(nextSession.access_token);
      settle(nextSession);
    });

    const restoreVersion = authEventVersionRef.current;
    const restoreSession = async () => {
      try {
        const { data: { session: restored }, error } = await supabase.auth.getSession();
        if (error) throw error;

        let readySession = restored;
        const expiresAtMs = restored?.expires_at ? restored.expires_at * 1000 : 0;
        if (restored && expiresAtMs <= Date.now() + 60_000) {
          const { data, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) throw refreshError;
          readySession = data.session;
        }

        if (readySession) supabase.realtime.setAuth(readySession.access_token);

        // A sign-in/sign-out/refresh that happened while restore was running
        // is newer and must never be overwritten by the restore result.
        if (authEventVersionRef.current === restoreVersion) settle(readySession);
      } catch (error) {
        console.warn("Initial session restore failed:", error);
        if (authEventVersionRef.current === restoreVersion) settle(null);
      }
    };

    void restoreSession();

    // Data pages use one coordinated resume signal instead of each firing its
    // own request as soon as a suspended tab becomes visible. A long-suspended
    // tab must refresh first; otherwise all mounted loaders race the auth
    // client with an expired token and can leave the UI stuck until reload.
    let hiddenAt: number | null = document.visibilityState === "hidden" ? Date.now() : null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }

      const activeSession = currentSessionRef.current;
      if (!activeSession) return;

      const hiddenDuration = hiddenAt === null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      const expiresAtMs = activeSession.expires_at ? activeSession.expires_at * 1000 : 0;
      const needsRefresh = hiddenDuration >= 60_000 || expiresAtMs <= Date.now() + 120_000;

      if (!needsRefresh) {
        window.dispatchEvent(new CustomEvent("auth-session-ready"));
        return;
      }

      void coordinateAuthRecovery(async () => {
        try {
          const refresh = supabase.auth.refreshSession();
          const timeout = new Promise<never>((_, reject) => {
            window.setTimeout(() => reject(new Error("Foreground session refresh timed out")), 12_000);
          });
          const { data, error } = await Promise.race([refresh, timeout]);
          if (error) throw error;
          if (data.session) {
            supabase.realtime.setAuth(data.session.access_token);
            settle(data.session);
          }
        } catch (error) {
          console.warn("Foreground session recovery failed:", error);
        } finally {
          // Loaders now run after recovery, through requestWithAuthRecovery,
          // instead of stampeding the stale session during the refresh.
          window.dispatchEvent(new CustomEvent("auth-session-ready"));
        }
      });
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, user: session?.user ?? null, initializing }),
    [session, initializing],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
